// Determine mode for label selection
import React, { useState, useEffect, useCallback } from "react";
import { DefaultAndAuthProps } from "model/props";
import { useParams } from "react-router-dom";

import css from "./addsuborganisationsscreen.module.scss";
import PageHeader from "../../components/pageheader/PageHeader";
import { getLanguageString as _ } from "misc/lang";
import TextInput from "components/form/TextInput";
import FormButton from "components/form/FormButton";
import { Paths } from "appConfig";
import { Box, Alert, Snackbar } from "@mui/material";
import EmailAssign from "./EmailAssign";
import {
  apiHomeCreate,
  apiHomeUpdate,
  apiHomeDelete,
  apiAssignManager,
  apiRemoveManager,
  apiGetManagerList,
  apiHomeUpdateSigninQR,
  apiAccountCreate,
} from "net/requests";
import ConfirmDialog from "components/confirmdialog/ConfirmDialog";
import { Home } from "model/home";
import { saveLocal } from "misc/list_helper";
import QrPopup from "./QrPopup";
import { getQRImageDataURL, savePNGFile } from "misc/qr_utils";

const AddSubOrganisation: React.FC<DefaultAndAuthProps> = (props) => {
  const {
    navigate,
    admin,
    account,
    loader,
    homes,
    setHomes,
    setHome,
    home,
    globalAppMode,
  } = props;
  const { id } = useParams<{ id: string }>();
  const isEditMode = !!id;
  const mode = admin?.is_super_admin
    ? globalAppMode
    : account?.type?.toUpperCase();

  const [formData, setFormData] = useState({
    subOrganisationName: "",
    address: "",
    postalCode: "",
    location: "",
    processOwnerEmail: "",
    processAdminEmail: "",
    selectedPersonsOwners: [] as string[],
    selectedPersonsAdmins: [] as string[],
  });
  const [ownerEmailError, setOwnerEmailError] = useState<string>("");
  const [adminEmailError, setAdminEmailError] = useState<string>("");
  const [notification, setNotification] = useState<{
    open: boolean;
    message: string;
    severity: "success" | "error";
  }>({ open: false, message: "", severity: "success" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [originalOwners, setOriginalOwners] = useState<
    { email: string; uuid: string }[]
  >([]);
  const [originalAdmins, setOriginalAdmins] = useState<
    { email: string; uuid: string }[]
  >([]);
  const [editorItem, setEditorItem] = useState<Home | undefined>();
  const [showQr, setShowQr] = useState<boolean>(false);
  const [qrImgData, setQrImgData] = useState<string>("");

  const canManageOwners = !!(admin?.is_super_admin || admin?.is_account_admin);

  const loadSubOrganisationData = useCallback(async () => {
    if (!isEditMode || !id || !homes || !admin) return;

    const home = homes.find((h) => h.uuid === id);
    if (!home) return;
    setEditorItem({ ...home });
    const imageData = getQRImageDataURL(
      home?.signin_code || "qr-kod",
      true,
      10,
    );
    setQrImgData(imageData);

    // Load basic info
    let ownerEmails: string[] = [];
    let adminEmails: string[] = [];

    try {
      // Load Process Owners (Permission 2 - operation) only if allowed
      if (canManageOwners) {
        try {
          const ownersResp: any = await apiGetManagerList(
            admin,
            id,
            "operation",
          );
          const ownersData = ownersResp.data || [];
          ownerEmails = ownersData
            .map((o: any) => o.email_address)
            .filter((e: any) => !!e);
          setOriginalOwners(
            ownersData.map((o: any) => ({
              email: o.email_address,
              uuid: o.uuid,
            })),
          );
        } catch (err) {
          console.error("Error loading process owners:", err);
        }
      }
    } catch (err) {
      console.error("Error loading process owners:", err);
    }

    try {
      // Load Process Admins (Permission 3 - home)
      // Do not load or show process admins if the current user is a home manager
      if (!admin?.is_home_manager) {
        const adminsResp: any = await apiGetManagerList(admin, id, "home");
        const adminsData = adminsResp.data || [];
        adminEmails = adminsData
          .map((a: any) => a.email_address)
          .filter((e: any) => !!e);
        setOriginalAdmins(
          adminsData.map((a: any) => ({
            email: a.email_address,
            uuid: a.uuid,
          })),
        );
      }
    } catch (err) {
      console.error("Error loading process admins:", err);
    }

    setFormData({
      subOrganisationName: home.name || "",
      address: home.address_line1 || "",
      postalCode: home.address_postal_code || "",
      location: home.address_city || "",
      processOwnerEmail: "",
      processAdminEmail: "",
      selectedPersonsOwners: canManageOwners ? ownerEmails : [],
      // If current user is a home manager, don't expose admins
      selectedPersonsAdmins: admin?.is_home_manager ? [] : adminEmails,
    });
  }, [isEditMode, id, homes, admin, canManageOwners]);

  const handleAdd = async () => {
    if (!formData.subOrganisationName.trim()) {
      setNotification({
        open: true,
        message: "Sub-organisation name is required",
        severity: "error",
      });
      return;
    }

    if (!account?.uuid && !isEditMode) {
      setNotification({
        open: true,
        message: "Account information is missing",
        severity: "error",
      });
      return;
    }

    setIsSubmitting(true);
    loader.setLoading();

    try {
      let homeUuid: string | null = null;

      if (isEditMode && id) {
        const payload = {
          account_uuid: account?.uuid,
          name: formData.subOrganisationName,
          address_line1: formData.address,
          address_line2: "",
          address_region: "",
          address_postal_code: formData.postalCode,
          address_city: formData.location,
          country: "",
        };
        await apiHomeUpdate(admin, id, payload);
        homeUuid = id;
        // Update homes list
        if (homes && setHomes) {
          const updatedHomes = homes.map((h) =>
            h.uuid === id
              ? {
                  ...h,
                  ...payload,
                  account_uuid: payload.account_uuid ?? h.account_uuid,
                }
              : h,
          );
          setHomes(updatedHomes);
        }
      } else {
        // Create mode - use different API based on app mode
        if (mode !== "HEALTH") {
          // HR MODE - Use apiAccountCreate with parent_uuid
          const payload = {
            type: globalAppMode.toLowerCase(),
            name: formData.subOrganisationName,
            org_number: "",
            address_line1: formData.address,
            address_line2: "",
            address_city: formData.location,
            address_region: "",
            address_postal_code: formData.postalCode,
            family_parent_signin_code: "",
            parent_uuid: account?.uuid,
            country: "SE",
          };
          const createResp: any = await apiAccountCreate(admin, payload);
          homeUuid = createResp?.data?.uuid;
        } else {
          // HEALTH MODE - Use apiHomeCreate as before
          const payload = {
            account_uuid: account?.uuid,
            name: formData.subOrganisationName,
            address_line1: formData.address,
            address_line2: "",
            address_region: "",
            address_postal_code: formData.postalCode,
            address_city: formData.location,
            country: "",
          };
          const createResp: any = await apiHomeCreate(admin, payload);
          homeUuid = createResp?.data?.uuid;
        }
      }

      // Assign managers to the sub-organisation
      if (homeUuid) {
        // Process Owners (Permission 2 - operation) - only if allowed
        if (canManageOwners) {
          const originalOwnerEmails = originalOwners.map((o) => o.email);
          const newOwners = formData.selectedPersonsOwners.filter(
            (e) => !originalOwnerEmails.includes(e),
          );

          // Assign new Process Owners
          if (newOwners.length > 0) {
            const ownerResults = await Promise.all(
              newOwners.map((email) =>
                apiAssignManager(admin, homeUuid as string, "operation", email)
                  .then(() => ({ email, success: true }))
                  .catch((err) => ({ email, success: false, error: err })),
              ),
            );

            const failedOwners = ownerResults.filter((r: any) => !r.success);
            if (failedOwners.length) {
              console.error("Some owner assignments failed:", failedOwners);
            }
          }

          // Remove deleted Process Owners
          const removedOwners = originalOwners.filter(
            (o) => !formData.selectedPersonsOwners.includes(o.email),
          );

          if (removedOwners.length > 0) {
            const removeOwnerResults = await Promise.all(
              removedOwners.map((owner) =>
                apiRemoveManager(
                  admin,
                  homeUuid as string,
                  "operation",
                  owner.uuid,
                )
                  .then(() => ({ uuid: owner.uuid, success: true }))
                  .catch((err) => ({
                    uuid: owner.uuid,
                    success: false,
                    error: err,
                  })),
              ),
            );

            const failedOwnerRemovals = removeOwnerResults.filter(
              (r: any) => !r.success,
            );
            if (failedOwnerRemovals.length) {
              console.error("Some owner removals failed:", failedOwnerRemovals);
            }
          }
        }

        // Process Admins (Permission 3 - home)
        // Skip admin assignment/removal entirely if current user is a home manager
        if (!admin?.is_home_manager) {
          const originalAdminEmails = originalAdmins.map((a) => a.email);
          const newAdmins = formData.selectedPersonsAdmins.filter(
            (e) => !originalAdminEmails.includes(e),
          );

          // Assign new Process Admins
          if (newAdmins.length > 0) {
            const adminResults = await Promise.all(
              newAdmins.map((email) =>
                apiAssignManager(admin, homeUuid as string, "home", email)
                  .then(() => ({ email, success: true }))
                  .catch((err) => ({ email, success: false, error: err })),
              ),
            );

            const failedAdmins = adminResults.filter((r: any) => !r.success);
            if (failedAdmins.length) {
              console.error("Some admin assignments failed:", failedAdmins);
            }
          }

          // Remove deleted Process Admins
          const removedAdmins = originalAdmins.filter(
            (a) => !formData.selectedPersonsAdmins.includes(a.email),
          );

          if (removedAdmins.length > 0) {
            const removeAdminResults = await Promise.all(
              removedAdmins.map((adminUser) =>
                apiRemoveManager(
                  admin,
                  homeUuid as string,
                  "home",
                  adminUser.uuid,
                )
                  .then(() => ({ uuid: adminUser.uuid, success: true }))
                  .catch((err) => ({
                    uuid: adminUser.uuid,
                    success: false,
                    error: err,
                  })),
              ),
            );

            const failedAdminRemovals = removeAdminResults.filter(
              (r: any) => !r.success,
            );
            if (failedAdminRemovals.length) {
              console.error("Some admin removals failed:", failedAdminRemovals);
            }
          }
        }
      }

      loader.reset();
      setNotification({
        open: true,
        message:
          mode === "HEALTH"
            ? _(
                "organisation",
                isEditMode
                  ? "accommodation_update_success"
                  : "accommodation_create_success",
              )
            : mode === "HR"
            ? _(
                "organisation",
                isEditMode ? "suborg_update_success" : "suborg_create_success",
              )
            : _(
                "organisation",
                isEditMode ? "update_success" : "create_success",
              ),
        severity: "success",
      });
      setTimeout(() => {
        navigate(Paths.SUB_ORGANISATIONS);
      }, 1500);
    } catch (error) {
      loader.reset();
      console.error("Error saving sub-organisation:", error);
      setNotification({
        open: true,
        message:
          mode === "HEALTH"
            ? _(
                "organisation",
                isEditMode
                  ? "accommodation_update_error"
                  : "accommodation_create_error",
              )
            : mode === "HR"
            ? _(
                "organisation",
                isEditMode ? "suborg_update_error" : "suborg_create_error",
              )
            : _("organisation", isEditMode ? "update_error" : "create_error"),
        severity: "error",
      });
      setIsSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate(Paths.SUB_ORGANISATIONS);
  };

  const handleDelete = async () => {
    if (!id) return;

    try {
      await apiHomeDelete(admin, id);
      setNotification({
        open: true,
        message: _("organisation", "delete_sub_org_success"),
        severity: "success",
      });
      setDeleteDialog(false);
      setTimeout(() => {
        navigate(Paths.SUB_ORGANISATIONS);
      }, 1500);
    } catch (error) {
      console.error("Error deleting sub-organisation:", error);
      setNotification({
        open: true,
        message: _("organisation", "delete_sub_org_error"),
        severity: "error",
      });
      setDeleteDialog(false);
    }
  };

  const handleAddEmail = (type: "owners" | "admins") => {
    if (type === "owners" && !canManageOwners) return;
    const email =
      type === "owners"
        ? formData.processOwnerEmail.trim()
        : formData.processAdminEmail.trim();

    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!email) {
      type === "owners"
        ? setOwnerEmailError(_("organisation", "error_email_required"))
        : setAdminEmailError(_("organisation", "error_email_required"));
      return;
    }
    if (!emailRegex.test(email)) {
      type === "owners"
        ? setOwnerEmailError(_("organisation", "error_email_invalid"))
        : setAdminEmailError(_("organisation", "error_email_invalid"));
      return;
    }
    if (
      type === "owners"
        ? formData.selectedPersonsOwners.includes(email)
        : formData.selectedPersonsAdmins.includes(email)
    ) {
      type === "owners"
        ? setOwnerEmailError(_("organisation", "error_email_already_added"))
        : setAdminEmailError(_("organisation", "error_email_already_added"));
      return;
    }

    if (type === "owners") {
      setFormData((prev) => ({
        ...prev,
        selectedPersonsOwners: [...prev.selectedPersonsOwners, email],
        processOwnerEmail: "",
      }));
    } else {
      setFormData((prev) => ({
        ...prev,
        selectedPersonsAdmins: [...prev.selectedPersonsAdmins, email],
        processAdminEmail: "",
      }));
    }
  };

  const handleRemoveEmail = (
    emailToRemove: string,
    type: "owners" | "admins",
  ) => {
    if (type === "owners" && !canManageOwners) return;
    if (type === "owners") {
      setFormData((prev) => ({
        ...prev,
        selectedPersonsOwners: prev.selectedPersonsOwners.filter(
          (email) => email !== emailToRemove,
        ),
      }));
      return;
    }

    setFormData((prev) => ({
      ...prev,
      selectedPersonsAdmins: prev.selectedPersonsAdmins.filter(
        (email) => email !== emailToRemove,
      ),
    }));
  };

  // const actionSave = () => {
  //   if (!editorItem || !homes) return;

  //   if (!id || !admin) return;
  //   loader.setLoading();
  //   apiHomeUpdate(admin, id, editorItem)
  //     .then((response) => {
  //       loader.reset();
  //       saveLocal(homes, editorItem, setHomes);
  //       setHome(editorItem);
  //     })
  //     .catch((error) => {
  //       loader.reset();
  //       const errorCode = error.response?.data?.error;
  //       console.log(errorCode);
  //     });
  // };

  const actionUpdateSigninQR = () => {
    if (!home || !homes) return;

    loader.setLoading();
    apiHomeUpdateSigninQR(admin, home?.uuid)
      .then((response) => {
        loader.reset();
        saveLocal(homes, response.data, setHomes);
        setHome(response.data);
      })
      .catch((error) => {
        loader.reset();
        const errorCode = error.response?.data?.error;
        console.log(errorCode);
      });
  };

  useEffect(() => {
    loadSubOrganisationData();
  }, [isEditMode, id, homes, admin, loadSubOrganisationData]);

  return (
    <div className={css.container}>
      <PageHeader
        title={_(
          "organisation",
          isEditMode
            ? mode === "HEALTH"
              ? "health_edit_sub_title"
              : "edit_title"
            : mode === "HEALTH"
            ? "health_add_sub_organisation_title"
            : "add_sub_organisation_title",
        )}
        subtitle={_(
          "organisation",
          isEditMode
            ? "edit_subtitle"
            : mode === "HEALTH"
            ? "health_add_sub_organisation_subtitle"
            : "add_sub_organisation_subtitle",
        )}
        showDeleteButton={isEditMode && canManageOwners}
        onDelete={() => setDeleteDialog(true)}
        deleteButtonText={_("organisation", "delete_button")}
      />

      <div className={css.contentBox}>
        <Box
          display={"flex"}
          justifyContent={"space-between"}
          alignItems={"center"}
        >
          <div className={css.sectionTitle}>
            {_("organisation", "basic_information")}
          </div>
          {isEditMode && (
            <Box display="flex" gap={2}>
              <FormButton
                label={_("general", "btn_show_qr")}
                onSubmit={() => setShowQr(true)}
                variant={"contained"}
                inline={true}
                color={"primary"}
              />
            </Box>
          )}
        </Box>

        <div className={css.formGrid}>
          <TextInput
            label={_(
              "organisation",
              mode === "HEALTH"
                ? "health_sub_organisation_name"
                : "sub_organisation_name_label",
            )}
            labelOutside={true}
            initialValue={formData.subOrganisationName}
            updateValue={(value) =>
              setFormData({ ...formData, subOrganisationName: value })
            }
            placeholder={_(
              "organisation",
              mode === "HEALTH"
                ? "health_placeholder_sub_organisation_name"
                : "placeholder_sub_organisation_name",
            )}
            isRequired={true}
          />

          <TextInput
            label={_("organisation", "address")}
            labelOutside={true}
            initialValue={formData.address}
            updateValue={(value) =>
              setFormData({ ...formData, address: value })
            }
            placeholder={_("organisation", "placeholder_address")}
          />

          <TextInput
            label={_("organisation", "postal_code")}
            placeholder={_("organisation", "placeholder_postal_code")}
            initialValue={formData.postalCode}
            updateValue={(value) =>
              setFormData((prev) => ({ ...prev, postalCode: value }))
            }
            labelOutside={true}
          />

          <TextInput
            label={_("organisation", "location_label")}
            labelOutside={true}
            initialValue={formData.location}
            updateValue={(value) =>
              setFormData({ ...formData, location: value })
            }
            placeholder={_("organisation", "placeholder_location")}
          />
        </div>
      </div>

      {canManageOwners && (
        <div className={css.contentBox}>
          <EmailAssign
            title={_(
              "organisation",
              globalAppMode === "HEALTH"
                ? "health_process_owner_title_2"
                : "process_owner_title_2",
            )}
            subtitle={_("organisation", "assign_responsible_users")}
            inputValue={formData.processOwnerEmail}
            onInputChange={(value) => {
              setFormData((prev) => ({ ...prev, processOwnerEmail: value }));
              setOwnerEmailError("");
            }}
            onAdd={() => handleAddEmail("owners")}
            error={ownerEmailError}
            selectedEmails={formData.selectedPersonsOwners}
            onRemove={(email) => handleRemoveEmail(email, "owners")}
          />
        </div>
      )}

      {!admin?.is_home_manager && (
        <div className={css.contentBox}>
          <EmailAssign
            title={_(
              "organisation",
              globalAppMode === "HEALTH"
                ? "health_process_owner_title_3"
                : "process_owner_title_3",
            )}
            subtitle={_("organisation", "assign_responsible_users")}
            inputValue={formData.processAdminEmail}
            onInputChange={(value) => {
              setFormData((prev) => ({ ...prev, processAdminEmail: value }));
              setAdminEmailError("");
            }}
            onAdd={() => handleAddEmail("admins")}
            error={adminEmailError}
            selectedEmails={formData.selectedPersonsAdmins}
            onRemove={(email) => handleRemoveEmail(email, "admins")}
          />
        </div>
      )}

      <div className={css.buttonRow}>
        <FormButton
          label={_("organisation", "cancel")}
          onSubmit={handleCancel}
          variant="outlined"
          dontTransformToUppercase
          disabled={isSubmitting}
        />
        <FormButton
          label={_("organisation", isEditMode ? "save_changes" : "add_button")}
          onSubmit={handleAdd}
          dontTransformToUppercase
          disabled={isSubmitting}
        />
      </div>

      <ConfirmDialog
        open={deleteDialog}
        title={_("organisation", "confirm_delete_sub_org_title")}
        message={_("organisation", "confirm_delete_sub_org_message")}
        itemName={formData.subOrganisationName}
        confirmLabel={_("organisation", "confirm_delete_yes")}
        cancelLabel={_("organisation", "cancel")}
        onConfirm={handleDelete}
        onCancel={() => setDeleteDialog(false)}
      />

      <Snackbar
        open={notification.open}
        autoHideDuration={4000}
        onClose={() => setNotification({ ...notification, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={() => setNotification({ ...notification, open: false })}
          severity={notification.severity}
          sx={{ width: "100%" }}
        >
          {notification.message}
        </Alert>
      </Snackbar>
      {showQr && editorItem && (
        <QrPopup
          homeName={editorItem.name || ""}
          qrImgData={qrImgData}
          actionUpdateSigninQR={actionUpdateSigninQR}
          setShowQr={setShowQr}
          _={_}
          savePNGFile={savePNGFile}
        />
      )}
    </div>
  );
};

export default AddSubOrganisation;
